import { Grid } from '@mui/material';
import { QUERY_KEYS_SERVICE_AGREEMENTS } from 'api/query-keys';
import { companyService } from 'ogp/services';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'Routes';
import {
  Header,
  LocationFormInput,
  LwButton,
  LwFormInput,
  Page,
  Toolbox,
  useFormButtons,
} from 'redesign';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAlert } from 'shared/components/alerts';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { useZodSchema } from 'shared/hooks';
import { useMutation } from 'react-query';
import {
  CreateLimitedCompanyForm,
  createLimitedCompanySchema,
} from './create-limited-company.schema';
import { usePrefillLimitedCompanyFromCocNumber } from '../hooks/use-prefill-limited-company-from-coc-number';

export const CreateLimitedCompanyPage = () => {
  const { t } = useTranslation(['ogp-screen-specific', 'all-platforms-common', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const invalidateQuery = useInvalidateQuery();
  const schema = useZodSchema(
    createLimitedCompanySchema,
    'ogp-screen-specific',
    'all-platforms-common',
    'web-common'
  );
  const formMethods = useForm<CreateLimitedCompanyForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      cocNumber: '',
      companyName: '',
      location: undefined,
      invoiceEmail: '',
      profileImageUrl: '',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
    },
  });
  const { control } = formMethods;
  usePrefillLimitedCompanyFromCocNumber(formMethods);

  const createLimitedCompany = useMutation(companyService.createLimitedCompany, {
    onSuccess: () => {
      alertSuccess(
        t('collegial-lending.customers.create.success', { defaultValue: 'Klant aangemaakt' })
      );
      invalidateQuery(QUERY_KEYS_SERVICE_AGREEMENTS.serviceAgreementsForCompany());
      navigate(generatePath(routes.CollegialLendingCustomers));
    },
    onError: (error: Error) => alertError(error),
  });
  const { submitButtonProps, cancelButtonProps, returnTo } = useFormButtons(
    createLimitedCompany.status
  );

  const onSubmit = () => {
    formMethods.handleSubmit((data) => {
      createLimitedCompany.mutate({
        cocNumber: data.cocNumber,
        companyName: data.companyName,
        location: data.location,
        invoiceEmail: data.invoiceEmail,
        profileImageUrl: data.profileImageUrl || undefined,
        adminFirstName: data.adminFirstName,
        adminLastName: data.adminLastName,
        adminEmail: data.adminEmail,
      });
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={
          <Header
            titleText={t('collegial-lending.customers.add-customer', {
              defaultValue: 'Klant toevoegen',
            })}
          />
        }
        toolbox={
          <Toolbox underline>
            <LwButton onClick={onSubmit} iconColorMode="stroke" {...submitButtonProps}>
              {t('buttons.create', { ns: 'web-common' })}
            </LwButton>
            <LwButton
              onClick={returnTo(routes.CollegialLendingCustomers)}
              iconColorMode="fill"
              {...cancelButtonProps}
            >
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
          </Toolbox>
        }
      >
        <Grid container item spacing={2} xs={6} alignContent="flex-start">
          <Grid item xs={12}>
            <LwFormInput
              name="cocNumber"
              label={t('branch_offices.detail.table_headers.kvk_number', {
                ns: 'ogp-screen-specific',
              })}
              placeholder="12345678"
              control={control}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <LwFormInput
              name="companyName"
              label={t('collegial-lending.customers.create.company-name.label')}
              control={control}
              rules={{ required: true }}
            />
          </Grid>
          <LocationFormInput required />
          <Grid item xs={12}>
            <LwFormInput
              name="invoiceEmail"
              label={t('collegial-lending.customers.create.invoice-email.label')}
              control={control}
              inputType="email"
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <LwFormInput
              name="profileImageUrl"
              label={t('collegial-lending.customers.create.profile-image-url.label')}
              control={control}
              placeholder="https://"
            />
          </Grid>
          <Grid item xs={6}>
            <LwFormInput
              name="adminFirstName"
              label={t('worker_data.first_name', { ns: 'all-platforms-common' })}
              control={control}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <LwFormInput
              name="adminLastName"
              label={t('worker_data.last_name', { ns: 'all-platforms-common' })}
              control={control}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <LwFormInput
              name="adminEmail"
              label={t('worker_data.email', { ns: 'all-platforms-common' })}
              control={control}
              inputType="email"
              rules={{ required: true }}
            />
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
